import env from "config/env";
import {
  getAccessToken,
  getAccessTokenWithRefresh,
} from "hooks/use-update-token";
import {
  getZendeskTokenWithRefresh,
  reauthenticateZendesk,
  refreshZendeskToken,
} from "hooks/use-zendesk";
import wretch, { ConfiguredMiddleware } from "wretch";
import QueryStringAddon from "wretch/addons/queryString";

export const baseApi = wretch(env.apiUrl)
  .addon(QueryStringAddon)
  .defer((wretcher) => {
    try {
      const token = getAccessToken();
      return token ? wretcher.auth(`Bearer ${token}`) : wretcher;
    } catch {
      return wretcher;
    }
  })
  .content("application/json")
  .catcher(401, async (error, request) => {
    try {
      // Call your refresh token function
      const token = await getAccessTokenWithRefresh();
      if (!token) {
        throw error;
      }
      // Retry the request with the new token
      return request.auth(`Bearer ${token}`).fetch().json();
    } catch (refreshError) {
      throw error;
    }
  });

export const api = wretch(env.apiUrl)
  .addon(QueryStringAddon)
  .defer((wretcher) => {
    try {
      const token = getAccessToken();
      return token ? wretcher.auth(`Bearer ${token}`) : wretcher;
    } catch {
      return wretcher;
    }
  })
  .content("application/json")
  .catcher(401, async (error, request) => {
    try {
      const token = await getAccessTokenWithRefresh();
      if (!token) {
        throw error;
      }
      return request.auth(`Bearer ${token}`).fetch().json();
    } catch (refreshError) {
      throw error;
    }
  })
  .resolve((r) => r.json());

const withZendeskAuth = (options: any, token: string) => ({
  ...options,
  headers: { ...options.headers, Authorization: `Bearer ${token}` },
});

const zendeskAuth: ConfiguredMiddleware = (next) => async (url, options) => {
  const token = await getZendeskTokenWithRefresh();
  if (!token) {
    reauthenticateZendesk();
    throw new Error("Zendesk session expired");
  }

  let response: Response | undefined;
  try {
    response = await next(url, withZendeskAuth(options, token));
    if (response.status !== 401) return response;
  } catch (error) {
    if (!navigator.onLine) throw error;
  }

  const refreshed = await refreshZendeskToken();
  if (refreshed) {
    const retry = await next(url, withZendeskAuth(options, refreshed));
    if (retry.status !== 401) return retry;
  }
  reauthenticateZendesk();
  if (response) return response;
  throw new Error("Zendesk session expired");
};

export const zendeskApi = wretch(`${env.zendeskUrl}/api/v2`)
  .addon(QueryStringAddon)
  .middlewares([zendeskAuth])
  .content("application/json")
  .resolve((r) => r.json());

export const defaultFetcher = async (
  path: string | [url: string, query: any],
) => {
  if (typeof path === "string") return api.url(path).get();
  return api.url(path[0]).query(path[1]).get();
};
